import { useEffect, useState } from 'react';
import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { TaskStatusType } from '@/app/enums/TaskStatusType';
import { type ExportRequestDto } from '@/app/models/ExportRequestDto';
import { announceTaskSent, awaitTaskInModal, useDownloadTaskFile, useTask } from '../../task';
import { useStartExport } from '../core/_hooks';

type Params = {
  endpoint: string | undefined;
  /** Exportação grande: o modal fecha na hora em vez de esperar. */
  isLarge: boolean;
  /**
   * O modal está aberto. Depois de fechar, o conteúdo ainda fica montado durante o fade-out: nesse intervalo a
   * tarefa já é do aviso do canto, e baixar sozinho seria download automático fora do modal.
   */
  isOpen: boolean;
  onHide: () => void;
};

/**
 * Envio e espera da exportação pelo backend. Uma ação do usuário é um `POST`; daí em diante o modal só observa
 * a tarefa no cache de tarefas, o mesmo da aba Tarefas. Fechar o modal não cancela nada: a tarefa segue e avisa.
 */
export function useBackendExport({ endpoint, isLarge, isOpen, onHide }: Params) {
  const start = useStartExport(endpoint);
  const { mutate: downloadFile, isPending: isDownloading } = useDownloadTaskFile();
  const [awaitedName, setAwaitedName] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const awaited = useTask(awaitedName);

  // Enquanto o modal aberto espera, a conclusão é dele (baixa e fecha), não do aviso do canto.
  useEffect(() => (awaitedName && isOpen ? awaitTaskInModal(awaitedName) : undefined), [awaitedName, isOpen]);

  useEffect(() => {
    if (!isOpen || !awaited || awaited.status === TaskStatusType.RUNNING) return;
    setAwaitedName(undefined);
    if (awaited.status === TaskStatusType.COMPLETED) {
      downloadFile(awaited, { onSuccess: onHide });
    } else {
      // Fica no modal (ex.: "afine o filtro"), para ajustar e tentar de novo.
      setFailure(awaited.error ?? 'A exportação falhou.');
    }
  }, [awaited, downloadFile, isOpen, onHide]);

  /** `from`: de onde o chip parte rumo ao sino quando a exportação vai para segundo plano. */
  const submit = (request: ExportRequestDto, from: DOMRect) => {
    setFailure(undefined);
    start.mutate(request, {
      onSuccess: (taskName) => {
        if (!isLarge) {
          // Já, e não só no efeito: um evento SSE de conclusão pode chegar antes do próximo render.
          awaitTaskInModal(taskName);
          setAwaitedName(taskName);
          return;
        }
        onHide();
        announceTaskSent(from);
      },
    });
  };

  return {
    submit,
    isWaiting: start.isPending || !!awaitedName || isDownloading,
    /** Já dá para fechar o modal sem perder nada: a tarefa existe no backend. */
    canContinueInBackground: !!awaitedName,
    error: failure ?? (start.isError ? getErrorMessage(start.error) : undefined),
  };
}
